import { useTranslation } from "react-i18next";

import { Mascot } from "../components/Mascot";
import { Shot } from "../components/Shot";
import { useHasKey } from "../components/useHasKey";
import type { Mascot as MascotSpec } from "../components/mascots";
import type { Shot as ShotSpec } from "../components/shots";

export interface ShowcaseFrame {
  shot: ShotSpec;
  altKey: string;
}

export interface ShowcaseProps {
  id: string;
  /** The section numeral shown above the heading. A digit pair, never a word. */
  index: string;
  titleKey: string;
  bodyKey: string;
  /** Desktop frame first, phone frame second — the CSS sizes them by their own kind. */
  frames: readonly ShowcaseFrame[];
  /**
   * An optional figure standing to the LEFT of the frames. Both showcase bands now carry one
   * — the owner picked a pose per band by what the pose says about that band — and the
   * security section borrows the same grid for the same reason. Still optional, because the
   * phone-frame row above the platform cards uses this grid too and takes no figure.
   *
   * It is rendered before the frames in source order, which is what puts it in the first grid
   * column — no ordering property, so the DOM order and the visual order agree.
   */
  aside?: MascotSpec;
}

/**
 * A claim and the screen that backs it.
 *
 * The shape is deliberately the same in every instance: numeral, heading, one paragraph,
 * then the real product. Two of these carry most of the page's visual weight, and reusing
 * one component is what keeps them from drifting apart as copy changes.
 */
export function Showcase({ id, index, titleKey, bodyKey, frames, aside }: ShowcaseProps) {
  const { t } = useTranslation();
  const has = useHasKey();

  // A screenshot with no claim beside it is decoration, so the whole band goes with its
  // heading rather than leaving frames floating under nothing.
  if (!has(titleKey)) return null;

  return (
    <section className="section" id={id}>
      <div className="section__head">
        <span className="section__eyebrow">{index}</span>
        <h2 className="section__heading">{t(titleKey)}</h2>
        {has(bodyKey) ? <p className="section__lede">{t(bodyKey)}</p> : null}
      </div>

      <div className={`showcase__frames${aside ? " showcase__frames--aside" : ""}`}>
        {aside ? <Mascot className="showcase__aside" mascot={aside} /> : null}
        {frames.map((frame) => (
          <Shot key={frame.shot.name} shot={frame.shot} altKey={frame.altKey} />
        ))}
      </div>
    </section>
  );
}
